import { useMutation, UseMutationOptions } from "react-query";
import { fetchApi } from "../../../helpers/reactQueryApi.ts";
import { Activity } from "../../../@types/activities.ts";

export function useToggleRepliesClosed(
  activityId: string,
  repliesClosed?: boolean,
  options?: UseMutationOptions<{ replies_closed: boolean }, Error>,
) {
  return useMutation<{ replies_closed: boolean }, Error>(
    () =>
      fetchApi(`/activities/${activityId}/toggle_replies_closed`, {
        method: repliesClosed ? "DELETE" : "POST",
      }),
    options,
  );
}

export function useToggleNewsletterActivity(
  activityId: string,
  plannedForNewsletterAt?: string,
  options?: UseMutationOptions<unknown, Error>,
) {
  return useMutation<unknown, Error>(
    () =>
      fetchApi(
        `/activities/${activityId}/${
          plannedForNewsletterAt
            ? "remove_from_newsletter"
            : "add_to_newsletter"
        }`,
        {
          method: plannedForNewsletterAt ? "DELETE" : "POST",
        },
      ),
    options,
  );
}

export function useSetSticky(
  activityId: string,
  options?: UseMutationOptions<
    unknown,
    Error,
    "regular" | "confirmable" | boolean
  >,
) {
  return useMutation<unknown, Error, "regular" | "confirmable" | boolean>(
    (sticky) =>
      fetchApi(`/activities/${activityId}/sticky`, {
        method: sticky ? "POST" : "DELETE",
        body: { sticky },
      }),
    options,
  );
}

export function useToggleSubscriptionEmail(
  activityId: string,
  iSubscribed: boolean,
  options?: UseMutationOptions<unknown, Error>,
) {
  return useMutation(
    () =>
      fetchApi(
        `/activities/${activityId}/${
          iSubscribed ? "unsubscribe" : "subscribe"
        }.json?channel=email`,
        { method: "POST" },
      ),
    options,
  );
}

export function useCloseSurvey(
  activityId: string,
  type?: string,
  options?: UseMutationOptions<unknown, Error>,
) {
  return useMutation(
    () =>
      fetchApi(`/activities/${activityId}/close/${type}`, { method: "POST" }),
    options,
  );
}

export function useToggleBookmark(
  activityId: string,
  iBookmarked: boolean,
  options?: UseMutationOptions<unknown, Error>,
) {
  return useMutation(
    () =>
      fetchApi(
        `/activities/${activityId}/${
          iBookmarked ? "unbookmark" : "bookmark"
        }.json`,
        { method: "POST" },
      ),
    options,
  );
}

export function useToggleRecommendation(
  activityId: string,
  iRecommend: boolean,
  options?: UseMutationOptions<unknown, Error>,
) {
  return useMutation(
    () =>
      fetchApi(
        `/activities/${activityId}/recommendations${
          iRecommend ? "/" + Preload.current_member?.id : ""
        }.json`,
        { method: iRecommend ? "DELETE" : "POST" },
      ),
    options,
  );
}

export function useToggleHidden(
  activityId: string,
  hidden?: boolean,
  options?: UseMutationOptions<Activity, Error>,
) {
  // POST/DELETE /activities/:id/hide render the full activity JSON (like
  // #archive), so the caller can refresh its caches from the server response.
  return useMutation<Activity, Error>(
    () =>
      fetchApi(`/activities/${activityId}/hide`, {
        method: hidden ? "DELETE" : "POST",
      }),
    options,
  );
}

// POST /members/:id/ignore/:actorId answers with the updated list of ignored
// members (memberships/ignored.json.ruby), not with a redirect.
export function useIgnoreAuthor(
  actorId: string,
  options?: UseMutationOptions<unknown, Error>,
) {
  return useMutation(
    () =>
      fetchApi(`/members/${Preload.current_member?.id}/ignore/${actorId}`, {
        method: "POST",
      }),
    options,
  );
}

export function useToggleArchived(
  activityId: string,
  archived: boolean,
  options?: UseMutationOptions<unknown, Error>,
) {
  return useMutation(
    () =>
      fetchApi(`/activities/${activityId}/archive`, {
        method: archived ? "DELETE" : "POST",
      }),
    options,
  );
}

export function useDestroyActivity(
  activityId: string,
  options?: UseMutationOptions<unknown, Error>,
) {
  return useMutation(
    () =>
      fetchApi(`/activities/${activityId}`, {
        method: "DELETE",
      }),
    options,
  );
}
